// Project Detail Modal Component
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Edit, 
  Download, 
  Share, 
  ExternalLink, 
  Calendar, 
  MapPin, 
  Users, 
  DollarSign, 
  Mail, 
  Globe,
  FileText,
  FileDown,
  FileType,
  Database,
  Copy,
  MoreVertical,
  Trash2,
  Building
} from 'lucide-react';
import { Project } from '@/services/projectService';
import { format, parseISO } from 'date-fns';
import { toast } from '@/hooks/use-toast';

const categoryIcons = {
  'Reforestation': '🌲',
  'Wildlife Conservation': '🦎',
  'Water Conservation': '💧',
  'Renewable Energy': '⚡',
  'Waste Reduction': '♻️',
  'Soil Restoration': '🏔️',
  'Climate Research': '🧪',
  'Community Outreach': '❤️',
  'Other': '❓'
};

const statusColors = {
  'Planned': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  'Active': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  'Completed': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  'Paused': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  'Cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
};

interface ProjectDetailModalProps {
  project: Project | null;
  open: boolean;
  onClose: () => void;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
}

export function ProjectDetailModal({ project, open, onClose, onEdit, onDelete }: ProjectDetailModalProps) {
  if (!project) return null;

  const handleExportPDF = async () => {
    try {
      // We'll implement this PDF export function
      const pdfBlob = await generateProjectPDF(project);
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_project.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast({
        title: "PDF Downloaded",
        description: "Project details have been saved as a PDF.",
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Export Failed",
        description: `Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
    }
  };

  const handleExportWord = async () => {
    try {
      // We'll implement this Word export function
      const docBlob = await generateProjectWord(project);
      const url = URL.createObjectURL(docBlob);
  const a = document.createElement('a');
  a.href = url;
  // use .doc for HTML-based Word export to maximize compatibility
  a.download = `${project.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_project.doc`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast({
        title: "Word Document Downloaded",
        description: "Project details have been saved as a Word document.",
      });
    } catch (error) {
      console.error('Error generating Word document:', error);
      toast({
        title: "Export Failed",
        description: "Failed to generate Word document. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleExportMarkdown = () => {
    const markdown = generateProjectMarkdown(project);
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_project.md`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: "Markdown Downloaded",
      description: "Project details have been saved as a Markdown file.",
    });
  };

  const handleExportJSON = () => {
    const jsonData = JSON.stringify(project, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_project.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: "JSON Downloaded",
      description: "Project data has been saved as a JSON file.",
    });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Link Copied",
        description: "Project link has been copied to clipboard.",
      });
    } catch (error) {
      console.error('Failed to copy link:', error);
      toast({
        title: "Copy Failed", 
        description: "Failed to copy link. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                <span className="text-2xl">{categoryIcons[project.category]}</span>
                {project.name}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Badge className={statusColors[project.status]}>
                  {project.status}
                </Badge>
                <Badge variant="outline">{project.category}</Badge>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => onEdit(project)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              
              {/* Export Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Export Options</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleExportPDF}>
                    <FileText className="h-4 w-4 mr-2" />
                    PDF Document
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportWord}>
                    <FileType className="h-4 w-4 mr-2" />
                    Word Document
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportMarkdown}>
                    <FileDown className="h-4 w-4 mr-2" />
                    Markdown File
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportJSON}>
                    <Database className="h-4 w-4 mr-2" />
                    JSON Data
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* More Actions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleCopyLink}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Link
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDelete(project)}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Project
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </DialogHeader>

        {/* Project Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* Left Column - Main Details */}
          <div className="space-y-6">
            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground whitespace-pre-wrap">{project.description}</p>
              </CardContent>
            </Card>

            {/* Impact */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Expected Impact</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground whitespace-pre-wrap">{project.impact}</p>
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Start Date:</span>
                  <span className="text-sm text-muted-foreground">
                    {format(parseISO(project.start_date), 'PPP')}
                  </span>
                </div>
                {project.end_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">End Date:</span>
                    <span className="text-sm text-muted-foreground">
                      {format(parseISO(project.end_date), 'PPP')}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Additional Details */}
          <div className="space-y-6">
            {/* Project Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Project Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {project.budget && (
                  <div className="flex items-center gap-3">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Budget</p>
                      <p className="text-sm text-muted-foreground">${project.budget.toLocaleString()}</p>
                    </div>
                  </div>
                )}
                
                {project.participants && (
                  <div className="flex items-center gap-3">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Participants</p>
                      <p className="text-sm text-muted-foreground">{project.participants}</p>
                    </div>
                  </div>
                )}

                {project.location && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Location</p>
                      <p className="text-sm text-muted-foreground">{project.location}</p>
                    </div>
                  </div>
                )}

                {project.organization && (
                  <div className="flex items-center gap-3">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Organization</p>
                      <p className="text-sm text-muted-foreground">{project.organization}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contact Information */}
            {(project.contact_email || project.website_url) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {project.contact_email && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Email</p>
                        <a 
                          href={`mailto:${project.contact_email}`}
                          className="text-sm text-[#40e0d0] hover:underline"
                        >
                          {project.contact_email}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {project.website_url && (
                    <div className="flex items-center gap-3">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Website</p>
                        <a 
                          href={project.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#40e0d0] hover:underline flex items-center gap-1"
                        >
                          Visit Website
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Project Image */}
            {project.image_url && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Project Image</CardTitle>
                </CardHeader>
                <CardContent>
                  <img 
                    src={project.image_url} 
                    alt={project.name}
                    className="w-full h-48 object-cover rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <Separator className="my-6" />

        {/* Project Metadata */}
        <div className="text-xs text-muted-foreground text-center">
          Created: {format(parseISO(project.created_at), 'PPP')} • 
          Last Updated: {format(parseISO(project.updated_at), 'PPP')}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper functions for export functionality
function generateProjectMarkdown(project: Project): string {
  return `# ${project.name}

**Status:** ${project.status}
**Category:** ${project.category}
**Start Date:** ${format(parseISO(project.start_date), 'PPP')}
${project.end_date ? `**End Date:** ${format(parseISO(project.end_date), 'PPP')}` : ''}

## Description
${project.description}

## Expected Impact
${project.impact}

${project.organization ? `## Organization
${project.organization}` : ''}

${project.location ? `## Location
${project.location}` : ''}

${project.budget ? `## Budget
$${project.budget.toLocaleString()}` : ''}

${project.participants ? `## Participants
${project.participants}` : ''}

${project.contact_email ? `## Contact
Email: ${project.contact_email}` : ''}

${project.website_url ? `Website: ${project.website_url}` : ''}

---
*Generated from Myǃhū Projects on ${new Date().toLocaleDateString()}*
`;
}

// Placeholder functions - we'll implement these with proper libraries
async function generateProjectPDF(project: Project): Promise<Blob> {
  // Dynamic import to avoid bundling issues
  const { jsPDF } = await import('jspdf');

  // Helper: convert a Blob to dataURL
  const blobToDataURL = (blob: Blob) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  const maxWidth = pageWidth - (margin * 2);
  let yPosition = margin;

  // Title
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text(project.name, margin, yPosition);
  yPosition += 22;

  // Status / Category / Dates
  doc.setFontSize(11);
  doc.setFont(undefined, 'normal');
  doc.text(`Status: ${project.status}`, margin, yPosition);
  doc.text(`Category: ${project.category}`, margin + 250, yPosition);
  yPosition += 14;
  doc.text(`Start Date: ${format(parseISO(project.start_date), 'PPP')}`, margin, yPosition);
  if (project.end_date) {
    doc.text(`End Date: ${format(parseISO(project.end_date), 'PPP')}`, margin + 250, yPosition);
  }
  yPosition += 18;

  // If image exists, try to fetch and embed it (maintain aspect ratio)
  if (project.image_url) {
    try {
      const resp = await fetch(project.image_url);
      if (resp.ok) {
        const imgBlob = await resp.blob();
        const dataUrl = await blobToDataURL(imgBlob);

        // Estimate image dimensions while preserving aspect ratio
        // Create an offscreen image to get natural size
        const img = new Image();
        img.src = dataUrl;
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Failed to load project image'));
        });

        const imgMaxWidth = maxWidth;
        const scale = Math.min(imgMaxWidth / img.naturalWidth, 1);
        const imgWidth = img.naturalWidth * scale;
        const imgHeight = img.naturalHeight * scale;

        // If not enough space on page, add a new page
        if (yPosition + imgHeight + 20 > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }

        doc.addImage(dataUrl, 'JPEG', margin, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight + 12;
      }
    } catch (err) {
      // Ignore image embed failures but log
      // eslint-disable-next-line no-console
      console.warn('Failed to embed image in PDF:', err);
    }
  }

  // Description
  doc.setFont(undefined, 'bold');
  doc.text('Description:', margin, yPosition);
  yPosition += 14;
  doc.setFont(undefined, 'normal');
  const descriptionLines = doc.splitTextToSize(project.description || '', maxWidth);
  doc.text(descriptionLines, margin, yPosition);
  yPosition += descriptionLines.length * 12 + 10;

  // Expected Impact
  doc.setFont(undefined, 'bold');
  doc.text('Expected Impact:', margin, yPosition);
  yPosition += 14;
  doc.setFont(undefined, 'normal');
  const impactLines = doc.splitTextToSize(project.impact || '', maxWidth);
  doc.text(impactLines, margin, yPosition);
  yPosition += impactLines.length * 12 + 10;

  // Project Details
  if (project.organization || project.location || project.budget || project.participants) {
    doc.setFont(undefined, 'bold');
    doc.text('Project Details:', margin, yPosition);
    yPosition += 14;
    doc.setFont(undefined, 'normal');

    if (project.organization) {
      doc.text(`Organization: ${project.organization}`, margin, yPosition);
      yPosition += 12;
    }
    if (project.location) {
      doc.text(`Location: ${project.location}`, margin, yPosition);
      yPosition += 12;
    }
    if (project.budget) {
      doc.text(`Budget: $${project.budget.toLocaleString()}`, margin, yPosition);
      yPosition += 12;
    }
    if (project.participants) {
      doc.text(`Participants: ${project.participants}`, margin, yPosition);
      yPosition += 12;
    }
    yPosition += 6;
  }

  // Contact Information
  if (project.contact_email || project.website_url) {
    doc.setFont(undefined, 'bold');
    doc.text('Contact Information:', margin, yPosition);
    yPosition += 14;
    doc.setFont(undefined, 'normal');

    if (project.contact_email) {
      doc.text(`Email: ${project.contact_email}`, margin, yPosition);
      yPosition += 12;
    }
    if (project.website_url) {
      doc.text(`Website: ${project.website_url}`, margin, yPosition);
      yPosition += 12;
    }
  }

  // Footer
  doc.setFontSize(9);
  doc.setFont(undefined, 'italic');
  doc.text(`Generated from Myǃhū Projects on ${new Date().toLocaleDateString()}`, margin, pageHeight - margin / 2);

  // Convert to blob
  const pdfBlob = doc.output('blob');
  return pdfBlob;
}

async function generateProjectWord(project: Project): Promise<Blob> {
  // Generate an HTML-based Word document (.doc) which includes the image URL
  const html = `<!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <title>${project.name}</title>
      <style>
        body { font-family: Arial, Helvetica, sans-serif; padding: 20px; }
        h1 { font-size: 20pt; }
        .muted { color: #6b7280; }
        .section { margin-bottom: 16px; }
        .label { font-weight: bold; }
        img { max-width: 100%; height: auto; border-radius: 6px; }
      </style>
    </head>
    <body>
      <h1>${project.name}</h1>
      <div class="muted">Status: ${project.status} • Category: ${project.category}</div>
      <div class="muted">Start Date: ${format(parseISO(project.start_date), 'PPP')} ${project.end_date ? `• End Date: ${format(parseISO(project.end_date), 'PPP')}` : ''}</div>

      ${project.image_url ? `<div class="section"><img src="${project.image_url}" alt="${project.name}" /></div>` : ''}

      <div class="section">
        <div class="label">Description</div>
        <div>${project.description.replace(/\n/g, '<br/>')}</div>
      </div>

      <div class="section">
        <div class="label">Expected Impact</div>
        <div>${project.impact.replace(/\n/g, '<br/>')}</div>
      </div>

      <div class="section">
        <div class="label">Project Details</div>
        <div>
          ${project.organization ? `<div>Organization: ${project.organization}</div>` : ''}
          ${project.location ? `<div>Location: ${project.location}</div>` : ''}
          ${project.budget ? `<div>Budget: $${project.budget.toLocaleString()}</div>` : ''}
          ${project.participants ? `<div>Participants: ${project.participants}</div>` : ''}
        </div>
      </div>

      <div class="section">
        <div class="label">Contact</div>
        <div>
          ${project.contact_email ? `<div>Email: <a href="mailto:${project.contact_email}">${project.contact_email}</a></div>` : ''}
          ${project.website_url ? `<div>Website: <a href="${project.website_url}" target="_blank">${project.website_url}</a></div>` : ''}
        </div>
      </div>

      <div class="muted">Generated from Myǃhū Projects on ${new Date().toLocaleDateString()}</div>
    </body>
  </html>`;

  const blob = new Blob([html], { type: 'application/msword' });
  return blob;
}